// @ts-nocheck

import { createSelector } from 'reselect';

import { isBetshareEnabled } from '../../constants/features';
import { getIdFromProps, getOutcomeIdFromProps } from '../../selectors/_propsGetters_';
import { selectIsBetslipCashout } from '../../selectors/cashout/cashoutPrimary.selector';
import { selectEntitiesBets } from '../../selectors/entities/bets/entitiesBetsPrimary.selector';
import {
  selectEntitiesBetslipsEntity,
  selectEntitiesBetslipsEntityBetslipReference,
  selectEntitiesBetslipsEntityPendingCashout,
} from '../../selectors/entities/betslips/entitiesBetslipsPrimary.selector';
import { selectEntitiesCategories } from '../../selectors/entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesMatches } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOutcomes } from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { selectEntitiesTournaments } from '../../selectors/entities/tournaments/entitiesTournamentsPrimary.selector';
import {
  selectHistoryBetFromOutcomeId,
  selectHistoryCategoryFromOutcomeId,
  selectHistoryMatchFromOutcomeId,
  selectHistoryMatchIsLiveFromOutcomeId,
  selectHistoryOutcomeStatusFromOutcomeId,
  selectHistoryTournamentFromOutcomeId,
} from '../../selectors/history.selector';
import { selectRuntimeIsCashoutEnabled, selectRuntimeLicence } from '../../selectors/runtime/runtimePrimary.selector';
import { type BetState } from '../../states/entities/bets';
import { type BetslipState } from '../../states/entities/betslips';
import { type CategoryState } from '../../states/entities/categories';
import { MatchEntity } from '../../states/entities/matches';
import { type MatchState } from '../../states/entities/matches';
import { OutcomeEntity } from '../../states/entities/outcomes';
import { type OutcomeState } from '../../states/entities/outcomes';
import { type TournamentState } from '../../states/entities/tournaments';
import { type Licence, Licences } from '../../types/common';
import { type OutcomeStatus } from '../../types/history';
import { type SelectorMaker } from '../../types/selectors';
import { type Selector } from '../../types/selectors';
import { type SportId } from '../../types/sports';

const emptyArr = [];

type HistoryOutcomeInfoType = {
  outcome?: OutcomeState;
  bet?: BetState;
  match?: MatchState;
  isLive: boolean;
  status: OutcomeStatus;
  tournament?: TournamentState;
  category?: CategoryState;
};

export type HistoryOutcomeDetails = HistoryOutcomeInfoType & {
  outcomeId: number;
  odds: number;
};

type HistoryItemPropsType = {
  betslip?: BetslipState;
  cashoutStatus?: boolean | null;
  isChashoutEnabled: boolean;
  licence: Licence;
  isShareAvailable: boolean;
  betslipReference: string;
  showPendingCashoutInfosLabel: boolean;
  outcomes?: Array<HistoryOutcomeDetails>;
};

type HistoryOutcomeType = {
  outcomeLabel: string | null;
  outcomeHasOccured: boolean;
  betTitle: string | null;
  matchTitle: string | null;
  sportId: SportId;
  isBetbuilder: boolean;
};

export const makeHistoryEntitiesOutcomesEntitySelector: SelectorMaker<HistoryOutcomeType> = () => {
  const selectHistoryOutcome: Selector<OutcomeState | undefined> = createSelector(
    [selectEntitiesOutcomes, getIdFromProps],
    (outcomes, id) => outcomes[id],
  );

  const selectHistoryBet: Selector<BetState | null> = createSelector(
    [selectHistoryOutcome, selectEntitiesBets],
    (outcome, bets) => {
      if (outcome) {
        return bets[outcome.betId];
      }
      return null;
    },
  );

  const selectHistoryMatch: Selector<MatchState | null> = createSelector(
    [selectHistoryBet, selectEntitiesMatches],
    (bet, matches) => {
      if (bet) {
        return matches[bet.matchId];
      }
      return null;
    },
  );

  const selectHistoryMatchTitle: Selector<string | null> = createSelector(
    [selectHistoryMatch],
    (match) => (match && (match.title || match.matchTitle)) || null,
  );

  const selectHistoryMatchTournamentId: Selector<number | null> = createSelector(
    [selectHistoryMatch],
    (match) => (match && match.tournamentId) || null,
  );

  const selectHistoryTournament: Selector<TournamentState | undefined> = createSelector(
    [selectHistoryMatchTournamentId, selectEntitiesTournaments],
    (tournamentId, tournaments) => tournaments[tournamentId],
  );

  const selectHistoryCategory: Selector<CategoryState | null> = createSelector(
    [selectHistoryTournament, selectEntitiesCategories],
    (tournament, categories) => {
      if (tournament) {
        return categories[tournament.categoryId] ?? null;
      }
      return null;
    },
  );

  const selectHistoryBetTitle: Selector<string | null> = createSelector(
    [selectHistoryBet],
    (bet) => (bet && bet.betTitle) || null,
  );

  const selectHistoryOutcomeLabel: Selector<string | null> = createSelector(
    [selectHistoryOutcome],
    (outcome) => (outcome && outcome.label) || null,
  );

  const selectHistoryOutcomeHasOccured: Selector<boolean> = createSelector(
    [selectHistoryOutcome],
    (outcome) => outcome?.hasOccured ?? false,
  );

  const selectHistoryIsABetbuilderOutcome: Selector<boolean> = createSelector([selectHistoryOutcome], (outcome) => {
    if (outcome) {
      return !!outcome.subOutcomes;
    }
    return false;
  });

  return createSelector(
    [
      selectHistoryOutcomeLabel,
      selectHistoryOutcomeHasOccured,
      selectHistoryBetTitle,
      selectHistoryMatchTitle,
      selectHistoryCategory,
      selectHistoryIsABetbuilderOutcome,
    ],
    (outcomeLabel, outcomeHasOccured, betTitle, matchTitle, category, isBetbuilder) => {
      return {
        outcomeLabel,
        outcomeHasOccured,
        betTitle,
        matchTitle,
        sportId: category?.sportId ?? 0,
        isBetbuilder,
      };
    },
  );
};

const makeHistoryOutcomeInfoSelector: SelectorMaker<HistoryOutcomeInfoType> = () => {
  const selectHistoryOutcome = makeHistoryEntitiesOutcomesEntitySelector();

  const selectGlobalOutcome: Selector<OutcomeState | undefined> = createSelector(
    [selectEntitiesOutcomes, getOutcomeIdFromProps],
    (outcomes, outcomeId) => outcomes[outcomeId],
  );

  const selectOutcome: Selector<OutcomeState | undefined> = createSelector(
    [selectHistoryOutcome, selectGlobalOutcome],
    (historyOutcome, globalOutcome) => ({ ...globalOutcome, ...historyOutcome }),
  );

  const selectGlobalBet: Selector<BetState | undefined> = createSelector(
    [selectEntitiesBets, selectGlobalOutcome],
    (bets, outcome) => bets[outcome?.betId],
  );

  const selectBet: Selector<BetState | undefined> = createSelector(
    [selectHistoryBetFromOutcomeId, selectGlobalBet],
    (historyBet, globalBet) => ({ ...globalBet, ...historyBet }),
  );

  const selectGlobalMatch: Selector<MatchState | undefined> = createSelector(
    [selectEntitiesMatches, selectGlobalBet],
    (matches, bet) => matches[bet?.matchId],
  );

  const selectMatch: Selector<MatchState | undefined> = createSelector(
    [selectHistoryMatchFromOutcomeId, selectGlobalMatch],
    (historyMatch, globalMatch) => ({
      ...historyMatch,
      ...globalMatch,
    }),
    // Scores in live not update in history entities
  );

  const selectGlobalMatchIsLive: Selector<boolean> = createSelector([selectGlobalMatch], (match) =>
    MatchEntity.isLive(match),
  );

  const selectIsMatchLive: Selector<boolean> = createSelector(
    [selectHistoryMatchIsLiveFromOutcomeId, selectGlobalMatchIsLive],
    (historyMatchIsLive, globalMatchIsLive) => historyMatchIsLive ?? globalMatchIsLive,
  );

  const selectGlobalStatus: Selector<OutcomeStatus> = createSelector(
    [selectGlobalOutcome, selectGlobalMatchIsLive],
    (outcome, isLive) => OutcomeEntity.getStatus(outcome, isLive),
  );

  const selectStatus: Selector<OutcomeStatus> = createSelector(
    [selectHistoryOutcomeStatusFromOutcomeId, selectGlobalStatus],
    (historyStatus, globalStatus) => historyStatus ?? globalStatus,
  );

  const selectGlobalTournament: Selector<TournamentState | undefined> = createSelector(
    [selectEntitiesTournaments, selectGlobalMatch],
    (tournaments, match) => tournaments[match?.tournamentId],
  );

  const selectTournament: Selector<TournamentState | undefined> = createSelector(
    [selectHistoryTournamentFromOutcomeId, selectGlobalTournament],
    (historyTournament, globalTournament) => ({ ...globalTournament, ...historyTournament }),
  );

  const selectGlobalCategory: Selector<CategoryState | undefined> = createSelector(
    [selectEntitiesCategories, selectGlobalTournament],
    (categories, tournament) => categories[tournament?.categoryId],
  );

  const selectCategory: Selector<CategoryState | undefined> = createSelector(
    [selectHistoryCategoryFromOutcomeId, selectGlobalCategory],
    (historyCategory, globalCategory) => ({ ...globalCategory, ...historyCategory }),
  );

  // use of `??` in order to have the global state fallback when we access to the history from the WinModal.
  // Without it, we only receive part of the `entities` because we are not on the history rub

  return createSelector(
    [selectOutcome, selectBet, selectMatch, selectIsMatchLive, selectStatus, selectTournament, selectCategory],
    (outcome, bet, match, isLive, status, tournament, category) => ({
      outcome,
      bet,
      match,
      isLive,
      status,
      tournament,
      category,
    }),
  );
};

const makeHistoryItemPropsSelector: SelectorMaker<HistoryItemPropsType> = () => {
  const selectIsShareEnabled: Selector<boolean> = createSelector(
    selectEntitiesBetslipsEntityBetslipReference,
    (betslipReference) => isBetshareEnabled && !!betslipReference,
  );

  const selectShowPendingCashoutInfosLabel: Selector<boolean> = createSelector(
    [selectEntitiesBetslipsEntityPendingCashout, selectRuntimeLicence],
    (pendingCashout, licence) => pendingCashout && licence === Licences.DE,
  );

  const selectHistoryOutcomes: Selector<Array<HistoryOutcomeDetails> | undefined> = createSelector(
    [(state) => state, selectEntitiesBetslipsEntity],
    (state, betslip) => {
      const selectHistoryOutcomeInfo = makeHistoryOutcomeInfoSelector();

      return (
        betslip?.details?.map((detail) => ({
          ...detail,
          ...selectHistoryOutcomeInfo(state, detail),
        })) ?? emptyArr
      );
    },
  );

  return createSelector(
    [
      selectEntitiesBetslipsEntity,
      selectEntitiesBetslipsEntityBetslipReference,
      selectIsBetslipCashout,
      selectRuntimeIsCashoutEnabled,
      selectRuntimeLicence,
      selectIsShareEnabled,
      selectShowPendingCashoutInfosLabel,
      selectHistoryOutcomes,
    ],
    (
      betslip,
      betslipReference,
      cashoutStatus,
      isCashoutEnabled,
      licence,
      isShareAvailable,
      showPendingCashoutInfosLabel,
      outcomes,
    ) => ({
      betslip,
      cashoutStatus,
      isCashoutEnabled,
      licence,
      isShareAvailable,
      betslipReference,
      showPendingCashoutInfosLabel,
      outcomes,
    }),
  );
};

export default makeHistoryItemPropsSelector;
